import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import { SwitchField } from '@/ui/rapid-ui'
import {
	type OrganisationAtsAutomationSettingsData,
	useOrganisationAtsAutomationSettingsShow,
	useOrganisationAtsAutomationSettingsUpdate,
} from '@jobilla/nexus-sdk'
import { CheckIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type AtsAutomationSetting = keyof OrganisationAtsAutomationSettingsData

export function AtsAutomationSettings() {
	const { t } = useTranslation('common')
	const jobillaOrgId = useJobillaOrgId()

	const { data: settings, isPending, isError, isFetching, refetch } = useOrganisationAtsAutomationSettingsShow(
		jobillaOrgId,
		{ enabled: !!jobillaOrgId },
	)
	const { mutateAsync: updateSettings, isPending: isSaving, isError: saveFailed } =
		useOrganisationAtsAutomationSettingsUpdate()

	const [justSavedSetting, setJustSavedSetting] = useState<AtsAutomationSetting | null>(null)
	const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const toggle = async (setting: AtsAutomationSetting) => {
		if (!jobillaOrgId || !settings || isSaving) { return }
		try {
			await updateSettings({
				organisation: jobillaOrgId,
				data: { [setting]: !settings[setting] },
			})
			await refetch()
			setJustSavedSetting(setting)
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
			savedTimerRef.current = setTimeout(() => setJustSavedSetting(null), 2000)
		} catch (err) {
			logger.error(err, { tags: { component: 'AtsAutomationSettings', action: 'update' } })
		}
	}

	if (isError) {
		return <p className="mt-4 text-sm text-red-600">{t('exportCenter.automation.loadError')}</p>
	}

	return (
		<div className="mt-4 space-y-3 max-w-[680px]">
			<SwitchField
				checked={settings?.autoGenerateCampaigns ?? false}
				onChange={() => void toggle('autoGenerateCampaigns')}
				disabled={isSaving || isFetching}
				isLoading={isPending}
				label={t('exportCenter.automation.autoGenerateLabel')}
				description={t('exportCenter.automation.autoGenerateDescription')}
				labelAddon={<SavedBadge visible={justSavedSetting === 'autoGenerateCampaigns'} />}
			/>
			<SwitchField
				checked={settings?.autoCloseCampaigns ?? false}
				onChange={() => void toggle('autoCloseCampaigns')}
				disabled={isSaving || isFetching}
				isLoading={isPending}
				label={t('exportCenter.automation.autoCloseLabel')}
				description={t('exportCenter.automation.autoCloseDescription')}
				labelAddon={<SavedBadge visible={justSavedSetting === 'autoCloseCampaigns'} />}
			/>
			{saveFailed
				? <p className="text-sm text-red-600">{t('exportCenter.automation.saveError')}</p>
				: null}
		</div>
	)
}

function SavedBadge({ visible }: { visible: boolean }) {
	const { t } = useTranslation('common')

	return (
		<AnimatePresence>
			{visible
				? (
					<motion.span
						key="saved"
						initial={{ opacity: 0, scale: 0.85 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.85 }}
						transition={{ duration: 0.15 }}
						className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-200"
					>
						<CheckIcon className="size-3" strokeWidth={2.5} />
						{t('exportCenter.automation.saved')}
					</motion.span>
				)
				: null}
		</AnimatePresence>
	)
}
